import React, { lazy, Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { useAuthStore } from '@rfacil/shared/services';
import LoginForm from './components/LoginForm';
import OlvideContrasena from './components/OlvideContrasena';
import RestablecerPassword from './components/RestablecerPassword';
import CambioPassword from './components/CambioPassword';
import AuthRedirect from './components/AuthRedirect';

import { PermissionRouteGuard } from './components/PermissionRouteGuard';
import { CustomSnackbar, Loader, InactivityDialog } from '@rfacil/shared/ui';
import { InstallPrompt } from './components/InstallPrompt';
import { GestionSesion } from '@rfacil/shared/services';
const GestionCorporativos = lazy(() => import('./pages/GestionCorporativo'));
const GestionEmpresa = lazy(() => import('./pages/GestionEmpresa'));
const GestionUsuarios = lazy(() => import('./pages/GestionUsuario'));
const GestionRoles = lazy(() => import('./pages/GestionRol'));
const GestionSistema = lazy(() => import('./pages/GestionSistema'));
const GestionTipoDocumento= lazy(() => import('./pages/GestionTipoDocumento'));
const GestionTipoProveedor = lazy(() => import('./pages/GestionTipoProveedor'));
const GestionRelacionProveedorUsuario = lazy(() => import('./pages/GestionRelacionProvUser'));
const GestionRelacionFacturaManual = lazy(() => import('./pages/GestionRelacionFacturaManual'));
const GestionParametrosEmpresa = lazy(() => import('./pages/GestionParametrosEmpresa'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MaterialidadApp = lazy(() => import('../materialidad/app/app'));
/**
 * @component ProtectedRoute
 * @description Un componente que envuelve las rutas para protegerlas del acceso no autenticado.
 * Comprueba si el usuario está autenticado y muestra un spinner de carga mientras comprueba.
 * Si el usuario no está autenticado, lo redirige a la página de inicio de sesión.
 * @param {object} props Las propiedades del componente.
 * @param {React.ReactNode} props.children Los componentes secundarios a renderizar si el usuario está autenticado.
 * @returns {React.ReactElement} El componente renderizado.
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

/**
 * @component AppContent
 * @description El componente principal que define las rutas de la aplicación.
 * Utiliza `react-router` (v7) para manejar la navegación y renderiza diferentes componentes según la URL.
 * También utiliza el componente `ProtectedRoute` para proteger las rutas que requieren autenticación.
 * @returns {React.ReactElement} El componente renderizado.
 */
const AppContent: React.FC = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const pendingPasswordChange = useAuthStore((s) => s.pendingPasswordChange);
  const showVoluntaryPasswordChange = useAuthStore(
    (s) => s.showVoluntaryPasswordChange
  );

  return (
    <>
      <GestionSesion />
      <InactivityDialog />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route
            path="/login"
            element={
              <AuthRedirect>
                <LoginForm />
              </AuthRedirect>
            }
          />
          <Route
            path="/olvide-contrasena"
            element={
              <AuthRedirect>
                <OlvideContrasena />
              </AuthRedirect>
            }
          />
          <Route
            path="/restablecer-contrasena"
            element={
              <AuthRedirect>
                <RestablecerPassword />
              </AuthRedirect>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-rol"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Roles" allowedRoles={['Empresa']}>
                  <GestionRoles />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
            <Route
              path="/gestionar-relacion-factura-manual"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="RelacionFacturaManual" allowedRoles={['Empresa']}>
                    <GestionRelacionFacturaManual />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gestionar-tipo-documento"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="GestionarTipoDocumento" allowedRoles={['Operativo']}>
                    <GestionTipoDocumento />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/gestionar-tipo-proveedor"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="TipoProveedor" allowedRoles={['Operativo']}>
                    <GestionTipoProveedor />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />
              <Route
              path="/gestionar-relacion-proveedor-usuario"
              element={
                <ProtectedRoute>
                  <PermissionRouteGuard requiredSection="RelacionUsuarioProveedor" allowedRoles={['Operativo']}>
                    <GestionRelacionProveedorUsuario />
                  </PermissionRouteGuard>
                </ProtectedRoute>
              }
            />

          <Route
            path="/gestionar-sistema"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="GestionSistema">
                  <GestionSistema />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />

          <Route
            path="/gestionar-corporativo"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Corporativos" allowedRoles={['Super']}>
                  <GestionCorporativos />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-empresas"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Empresas" allowedRoles={['Corporativo']}>
                  <GestionEmpresa />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-parametros-empresa"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Empresas" allowedRoles={['Corporativo']}>
                  <GestionParametrosEmpresa />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gestionar-usuarios"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard requiredSection="Usuarios" allowedRoles={['Super', 'Corporativo', 'Empresa']}>
                  <GestionUsuarios />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/materialidad/*"
            element={
              <ProtectedRoute>
                <PermissionRouteGuard
                  requiredSection="ListadoFacturas"
                  alternativeSection="FacturasVisor"
                >
                  <MaterialidadApp />
                </PermissionRouteGuard>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <CustomSnackbar />
      <InstallPrompt />
      {isAuthenticated &&
      (pendingPasswordChange || showVoluntaryPasswordChange) ? (
        <CambioPassword />
      ) : null}
    </>
  );
};

/**
 * @function App
 * @description El componente raíz de la aplicación.
 * Envuelve el componente `AppContent` con el `AuthProvider` para proporcionar el contexto de autenticación a todos los componentes secundarios.
 * @returns {React.ReactElement} El componente renderizado.
 */
export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
